type CountryRaw = Record<string, unknown>;

type CountryValue = string | CountryRaw;

type CountryItem = {
  code: string;
  name: string;
  enName: string;
  region: string;
  flagImg: string;
  wmcode: string;
  flagEmoji: string;
  aliases: string[];
  raw: CountryRaw;
};

const normalizeNameValue = (value: unknown): string | undefined => {
  if (typeof value === 'string') return value;
  if (value && typeof value === 'object') {
    const objectValue = value as Record<string, unknown>;
    return (objectValue.common as string) || (objectValue.official as string) || (objectValue.native as string);
  }
  return undefined;
};

const collectStrings = (value: unknown): string[] => {
  if (!value) return [];
  if (typeof value === 'string') return [value];
  if (Array.isArray(value)) return value.filter(item => typeof item === 'string') as string[];
  if (typeof value === 'object') {
    return Object.values(value).filter(item => typeof item === 'string') as string[];
  }
  return [];
};

export const normalizeCountry = (raw: CountryRaw): CountryItem => {
  const record = raw as Record<string, unknown>;
  const aliasSet = new Set<string>();
  const addAlias = (value: unknown) => {
    collectStrings(value).forEach(item => {
      if (item.trim()) aliasSet.add(item);
    });
  };

  addAlias(record.name);
  addAlias(record.enName);
  addAlias(record.countryName);
  addAlias(record.countryNameEn);
  addAlias(record.cnName);
  addAlias(record.nameEn);
  addAlias(record.nameCn);
  addAlias(record.zhName);
  addAlias(record.shortName);
  addAlias(record.shortNameEn);
  addAlias(record.shortNameCn);
  addAlias(record.abbr);
  addAlias(record.abbr2);
  addAlias(record.abbr3);
  addAlias(record.alpha2Code);
  addAlias(record.alpha3Code);
  addAlias(record.iso);
  addAlias(record.iso2);
  addAlias(record.iso3);
  addAlias(record.isoCode);
  addAlias(record.cca2);
  addAlias(record.cca3);
  addAlias(record.cioc);
  addAlias(record.fifa);
  addAlias(record.numericCode);
  addAlias(record.phoneCode);
  addAlias(record.callingCode);
  addAlias(record.dialCode);
  addAlias(record.altSpellings);

  if (record.translations && typeof record.translations === 'object') {
    Object.values(record.translations as Record<string, unknown>).forEach(value => {
      addAlias(value);
      if (value && typeof value === 'object') {
        const valueRecord = value as Record<string, unknown>;
        addAlias(valueRecord.common);
        addAlias(valueRecord.official);
        addAlias(valueRecord.native);
      }
    });
  }

  return {
    code:
      (record.code as string) ||
      (record.countryCode as string) ||
      (record.isoCode as string) ||
      (record.iso2 as string) ||
      (record.iso as string) ||
      (record.alpha2Code as string) ||
      '',
    name:
      normalizeNameValue(record.name) ||
      normalizeNameValue(record.countryName) ||
      normalizeNameValue(record.cnName) ||
      normalizeNameValue(record.countryCnName) ||
      normalizeNameValue(record.nameCn) ||
      normalizeNameValue(record.zhName) ||
      '',
    enName:
      normalizeNameValue(record.enName) ||
      normalizeNameValue(record.countryNameEn) ||
      normalizeNameValue(record.nameEn) ||
      normalizeNameValue(record.englishName) ||
      '',
    region: (record.region as string) || (record.continent as string) || (record.area as string),
    flagImg: (record.flagImg as string) || '',
    wmcode: (record.wmcode as string) || '',
    flagEmoji:
      (record.flagEmoji as string) ||
      (typeof record.flag === 'string' && !record.flag.startsWith('http') ? record.flag : undefined) ||
      '',
    aliases: Array.from(aliasSet),
    raw,
  };
};

export const getCountryDisplayName = (item: CountryItem) => {
  return item.name || item.enName || item.code || '';
};

export const getCountrySearchText = (item: CountryItem) => {
  const parts = [item.name, item.enName, item.code, ...(item.aliases || [])].filter(Boolean) as string[];
  return parts.join(' ').toLowerCase();
};

export const isCountryValue = (item: unknown): item is CountryValue => {
  if (typeof item === 'string') {
    return item.trim().length > 0;
  }
  return typeof item === 'object' && item !== null && !Array.isArray(item);
};

export const normalizeValueArray = (input: CountryValue | CountryValue[] | null | undefined): CountryValue[] => {
  if (Array.isArray(input)) {
    return input.filter(isCountryValue);
  }
  return isCountryValue(input) ? [input] : [];
};

export type { CountryItem, CountryRaw, CountryValue };
